import { useObservableValue } from "@iventis/utilities";
import React, { useContext, useRef } from "react";
import { HeaderRenderer } from "../interactive-grid.types";
import { IGridContext } from "../state/interactive-grid-context";
import { findGroup, getRowBorder } from "../utilities/helpers";

const IGridHeaderVerticalBase: React.FC<{
    width: { venue: number; discipline: number };
    offsetY: number;
    totalWidth: number;
    renderFirstLevelRowHeader: HeaderRenderer;
    renderSecondLevelRowHeader: HeaderRenderer;
    renderThirdLevelRowHeader: HeaderRenderer;
    hasBeenResized: boolean;
    setParentWidth: (width: { venue: number; discipline: number }) => void;
}> = ({ width: passedWidth, offsetY, totalWidth, renderFirstLevelRowHeader, renderSecondLevelRowHeader, renderThirdLevelRowHeader, hasBeenResized, setParentWidth }) => {
    const ctx = useContext(IGridContext);

    const layout = useObservableValue(ctx.getRowHeaderLayoutDistinct());

    const yTranslate = useObservableValue(ctx.yTranslation);

    const venueCount = useRef(0);
    const disciplineCount = useRef(0);
    const largestWidthVenue = useRef(0);
    const largestWidthDiscipline = useRef(0);

    const onVenueRowRender = (element) => {
        if (element && element.clientWidth > largestWidthVenue.current) {
            largestWidthVenue.current = element.clientWidth;
        }
        // eslint-disable-next-line no-plusplus
        venueCount.current++;
        if (venueCount.current === layout.length) {
            setParentWidth({ venue: largestWidthVenue.current, discipline: largestWidthDiscipline.current });
        }
    };

    const onDisciplineRowRender = (element) => {
        if (element && element.clientWidth > largestWidthDiscipline.current) {
            largestWidthDiscipline.current = element.clientWidth;
        }
        // eslint-disable-next-line no-plusplus
        disciplineCount.current++;
        if (disciplineCount.current === layout.length) {
            setParentWidth({ venue: largestWidthVenue.current, discipline: largestWidthDiscipline.current });
        }
    };

    return (
        <div
            style={{
                position: "absolute",
                pointerEvents: "none",
                width: "100%",
                transform: `translateY(${yTranslate}px)`,
                marginTop: `${offsetY}px`,
            }}
        >
            {layout?.map((row, topLevelRowIndex) =>
                row.wideCluster ? (
                    /* First level group */
                    <div
                        // eslint-disable-next-line no-plusplus
                        ref={() => venueCount.current++}
                        key={row.groupId}
                        style={{
                            height: `${row.height}px`,
                            width: `${totalWidth}px`,
                            backgroundColor: ctx.colours.value.firstLevelRowHeaderBackgroundColour,
                        }}
                    >
                        {renderFirstLevelRowHeader(findGroup(ctx.rowHeaderGroups.value || [], row.groupId))}
                    </div>
                ) : (
                    <div
                        key={row.groupId}
                        style={{
                            pointerEvents: "auto",
                            height: `${row.height}px`,
                            width: `max-content`,
                            display: "flex",
                            justifyContent: "stretch",
                            backgroundColor: ctx.colours.value.secondLevelRowHeaderBackgroundColour,
                        }}
                    >
                        {/* Second level group */}
                        <div
                            ref={onVenueRowRender}
                            style={{
                                pointerEvents: "auto",
                                height: `${row.height}px`,
                                width: hasBeenResized ? `${passedWidth.venue}px` : "auto",
                                minWidth: !hasBeenResized ? `${passedWidth.venue}px` : "auto",
                                overflow: "hidden",
                                ...(!layout[topLevelRowIndex - 1]?.wideCluster && { boxShadow: getRowBorder(ctx.colours.value.secondLevelRowHeaderLineColour) }),
                            }}
                        >
                            {renderSecondLevelRowHeader(findGroup(ctx.rowHeaderGroups.value || [], row.groupId))}
                        </div>
                        {/* Third level group */}
                        <div
                            ref={onDisciplineRowRender}
                            style={{
                                pointerEvents: "auto",
                                height: `${row.height}px`,
                                width: hasBeenResized ? `${passedWidth.discipline}px` : "auto",
                                minWidth: !hasBeenResized ? `${passedWidth.discipline}px` : "auto",
                                overflow: "hidden",
                                backgroundColor: ctx.colours.value.thirdLevelRowHeaderBackgroundColour,
                            }}
                        >
                            {row.children.map((child, childIndex) => (
                                <div
                                    key={child.groupId}
                                    style={{
                                        height: `${child.height}px`,
                                        ...(childIndex !== 0 && { boxShadow: getRowBorder(ctx.colours.value.thirdLevelRowHeaderLineColour) }),
                                        ...(childIndex === 0 &&
                                            !layout[topLevelRowIndex - 1]?.wideCluster && { boxShadow: getRowBorder(ctx.colours.value.secondLevelRowHeaderLineColour) }),
                                    }}
                                >
                                    {renderThirdLevelRowHeader(findGroup(ctx.rowHeaderGroups.value || [], child.groupId))}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}
        </div>
    );
};

export const IGridHeaderVertical = React.memo(IGridHeaderVerticalBase);
